/**
 * Admin view (/admin) — Phase 2 Wave 2.
 *
 * Только owner и admin могут попасть. Owner видит все CRUD-кнопки. Admin
 * видит таблицу + ассайнменты, но кнопки create/patch/delete users скрыты —
 * backend всё равно вернёт 403, на фронте просто нет смысла показывать.
 *
 * Содержит две вкладки:
 *   - Пользователи: таблица users + кнопки добавить/редактировать/удалить.
 *   - Портфели: таблица portfolios + CRUD.
 *
 * Назначение Менеджеров/Пользователей на конкретный проект делается через
 * Project Inspector (новые секции в project-inspector.js), не здесь.
 */

import { api } from '../api.js';
import { canDo, getCurrentUser } from '../auth.js';
import { showNotification } from '../components/notification.js';
import { openModal } from '../components/modal.js';
import { createInlinePicker } from '../components/inline-picker.js';

// Phase 2 Wave 3 (Б1: singleton): один экземпляр picker'а на module-init.
// document-listeners (pointerdown/keydown) регистрируются ОДИН раз.
const _picker = createInlinePicker();
document.body.appendChild(_picker.element);

const ROLE_LABEL = {
  owner:   'Собственник',
  admin:   'Админ',
  manager: 'Менеджер',
  user:    'Пользователь',
};

const ROLE_OPTIONS = [
  { value: 'manager', label: 'Менеджер' },
  { value: 'user',    label: 'Пользователь' },
  { value: 'admin',   label: 'Админ' },
  { value: 'owner',   label: 'Собственник' },
];

let _container = null;
let _users = [];
let _portfolios = [];
let _resources = [];
let _activeTab = 'users';
// Phase 2 Wave 4: matrix data — заполняется при первом открытии вкладки «Права».
let _matrixData = null;       // { actions, sections, matrix: {role: {action: bool}} }
let _visibilityData = {};     // portfolio_id → [{role, visible}]
// Phase 2 Wave 6: read-only вкладка «Подрядчики». Lazy-load при первом открытии.
let _contractorsAdmin = null;  // ContractorAdminRow[] или null если ещё не грузилось
let _showInactiveContractors = false;
// Ф-1: «Правила отбора» (префильтр Руслана). Lazy-load при первом открытии вкладки.
let _prefilterRules = null;    // { version, items } или null
// Wave 5 (П1.4): cache контракторов с TTL — picker дёргался каждый раз. Mentor-бот
// может привязывать снаружи, поэтому ставим TTL чтобы видеть свежие данные через
// разумное время. Локальные link/unlink инвалидируют кэш сразу.
let _contractorsCache = null;
let _contractorsCacheTime = 0;
const CONTRACTORS_TTL_MS = 15 * 60 * 1000;

async function ensureContractors() {
  const now = Date.now();
  if (_contractorsCache != null && now - _contractorsCacheTime < CONTRACTORS_TTL_MS) {
    return _contractorsCache;
  }
  _contractorsCache = await api.contractors({ unassigned: '1' });
  _contractorsCacheTime = now;
  return _contractorsCache;
}

function invalidateContractorsCache() {
  _contractorsCache = null;
  _contractorsCacheTime = 0;
}

export async function initAdmin(container) {
  _container = container;
  // Wave 4: вход в /admin — нужен user.read (Owner + Admin).
  if (!canDo('user.read')) {
    container.innerHTML = `
      <div style="padding:var(--space-6); color:var(--text-danger)">
        <h2>Доступ запрещён</h2>
        <p>Эта страница доступна только Собственнику и Админу.</p>
      </div>`;
    return;
  }
  await loadAll();
  render();
}

async function loadAll() {
  const [users, portfolios, resources] = await Promise.all([
    api.users({ include_inactive: 'true' }),
    api.portfolios(),
    api.resources(),
  ]);
  _users = users;
  _portfolios = portfolios;
  _resources = resources;
}

function render() {
  // Phase 2 Wave 4: вкладка «Права» только для Owner.
  const showPermissionsTab = canDo('system.permissions.update');
  const permissionsTabBtn = showPermissionsTab
    ? `<button class="admin-tab${_activeTab === 'permissions' ? ' active' : ''}" data-tab="permissions">Права</button>`
    : '';

  let body;
  if (_activeTab === 'users') body = renderUsers();
  else if (_activeTab === 'portfolios') body = renderPortfolios();
  else if (_activeTab === 'contractors') body = renderContractors();
  else if (_activeTab === 'resources') body = renderResources();
  else if (_activeTab === 'permissions' && showPermissionsTab) body = renderPermissions();
  else if (_activeTab === 'prefilter' && canDo('tender.read')) body = renderPrefilterRules();
  else body = renderUsers();

  // Wave 6: счётчик в кнопке-вкладке показывается только когда уже загрузили (lazy).
  const contractorsCount = _contractorsAdmin == null ? '' : ` (${_contractorsAdmin.length})`;

  _container.innerHTML = `
    <div class="admin-view" style="padding:var(--space-4);">
      <div class="admin-tabs" style="display:flex; gap:var(--space-2); margin-bottom:var(--space-4); border-bottom:1px solid var(--border);">
        <button class="admin-tab${_activeTab === 'users' ? ' active' : ''}" data-tab="users">Пользователи (${_users.length})</button>
        <button class="admin-tab${_activeTab === 'portfolios' ? ' active' : ''}" data-tab="portfolios">Портфели (${_portfolios.length})</button>
        <button class="admin-tab${_activeTab === 'contractors' ? ' active' : ''}" data-tab="contractors">Подрядчики${contractorsCount}</button>
        <button class="admin-tab${_activeTab === 'resources' ? ' active' : ''}" data-tab="resources">Ресурсы (${_resources.length})</button>
        ${canDo('tender.read') ? `<button class="admin-tab${_activeTab === 'prefilter' ? ' active' : ''}" data-tab="prefilter">Правила отбора</button>` : ''}
        ${permissionsTabBtn}
      </div>
      <div class="admin-tab-body">${body}</div>
    </div>
  `;
  _container.querySelectorAll('.admin-tab').forEach(btn => {
    btn.addEventListener('click', () => { _activeTab = btn.dataset.tab; render(); });
  });
  if (_activeTab === 'users') wireUsers();
  else if (_activeTab === 'portfolios') wirePortfolios();
  else if (_activeTab === 'contractors') wireContractors();
  else if (_activeTab === 'resources') wireResources();
  else if (_activeTab === 'permissions') wirePermissions();
  else if (_activeTab === 'prefilter') wirePrefilterRules();
}

// ---------- USERS ----------

function renderUsers() {
  // Wave 4: admin тоже может user.create (с role-aware guard).
  const canCreate = canDo('user.create');
  // Owner-only действия: contractor.link, contractor.unlink.
  const canLinkContractor = canDo('contractor.link');
  const headerBtn = canCreate
    ? `<button class="toolbar-btn-primary" data-action="add-user">+ Новый пользователь</button>`
    : '';
  return `
    <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:var(--space-3);">
      <h2 style="margin:0;">Пользователи</h2>
      ${headerBtn}
    </div>
    <table class="admin-table">
      <thead><tr>
        <th>Login</th><th>ФИО</th><th>Роль</th><th>Resource</th>
        <th>Контрактор</th>
        <th title="Каналы доставки задач: tg = Telegram, max = МАХ, tg+max = оба">Бот</th>
        <th>Активен</th><th>Last login</th><th></th>
      </tr></thead>
      <tbody>
        ${_users.map(u => renderUserRow(u, canCreate, canLinkContractor)).join('')}
      </tbody>
    </table>`;
}

function renderUserRow(u, canEdit, canLinkContractor) {
  const resName = u.resource_id
    ? (_resources.find(r => r.id === u.resource_id)?.name ?? `#${u.resource_id}`)
    : '—';
  const lastLogin = u.last_login_at ? new Date(u.last_login_at).toLocaleString('ru') : '—';
  // Wave 4: per-target hide — admin не редактирует admin/owner (backend всё равно вернёт 403,
  // но UI чище без disabled-кнопок).
  const isPrivilegedTarget = u.role === 'admin' || u.role === 'owner';
  const isOwner = canDo('user.deactivate') && canDo('contractor.link');  // только Owner проходит оба
  const editAllowed = canEdit && (isOwner || !isPrivilegedTarget);
  // Phase 2 Wave 3: контрактор-связка. В3: NULL-safe ?? '—'.
  const contractorCell = renderContractorCell(u, canLinkContractor);
  const actions = editAllowed
    ? `
        <button class="admin-btn-sm" data-action="edit-user" data-id="${u.id}">✏</button>
        <button class="admin-btn-sm" data-action="reset-pwd" data-id="${u.id}">🔑</button>
        ${u.active ? `<button class="admin-btn-sm danger" data-action="del-user" data-id="${u.id}">🗑</button>` : ''}
      `
    : '';
  // Wave 6: колонка «Бот» — тип канала доставки через linked contractor.
  // mentor_channels: 'tg' / 'max' / 'tg+max' / null
  const mentorCell = u.mentor_channels
    ? `<span class="mentor-on" title="Контрактор подключён через канал: ${u.mentor_channels}">${escapeHtml(u.mentor_channels)}</span>`
    : `<span class="mentor-off" title="Бот не подключён (нет linked contractor)">—</span>`;
  return `
    <tr class="${u.active ? '' : 'inactive'}">
      <td><code>${escapeHtml(u.login)}</code></td>
      <td>${escapeHtml(u.full_name)}</td>
      <td>${ROLE_LABEL[u.role] || u.role}</td>
      <td>${escapeHtml(resName)}</td>
      <td>${contractorCell}</td>
      <td>${mentorCell}</td>
      <td>${u.active ? '✓' : '✗'}</td>
      <td>${lastLogin}</td>
      <td><div class="admin-actions">${actions}</div></td>
    </tr>`;
}

function renderContractorCell(u, canLinkContractor) {
  // DB CHECK constraint: только manager/user могут иметь contractor_id.
  // Owner/admin — системные роли, для них колонка показывает прочерк.
  const eligible = u.role === 'manager' || u.role === 'user';
  if (!eligible) return '<span style="color:var(--text-tertiary)">—</span>';

  const name = u.contractor_display_name ?? '—';
  if (!canLinkContractor) return escapeHtml(name);

  const linkBtn = u.contractor_id
    ? `<button class="admin-btn-sm" data-action="unlink-contractor" data-id="${u.id}" title="Отвязать">🔗✕</button>`
    : `<button class="admin-btn-sm" data-action="link-contractor" data-id="${u.id}" title="Привязать контрактора">🔗</button>`;
  return `<span>${escapeHtml(name)}</span> ${linkBtn}`;
}

function wireUsers() {
  _container.querySelectorAll('[data-action]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const action = e.currentTarget.dataset.action;
      const id = Number(e.currentTarget.dataset.id);
      try {
        if (action === 'add-user') await onAddUser();
        else if (action === 'edit-user') await onEditUser(id);
        else if (action === 'reset-pwd') await onResetPwd(id);
        else if (action === 'del-user') await onDelUser(id);
        else if (action === 'link-contractor') await onLinkContractor(id, e.currentTarget);
        else if (action === 'unlink-contractor') await onUnlinkContractor(id);
      } catch (err) {
        showNotification(err.detail || err.message || 'Ошибка', { type: 'error' });
      }
    });
  });
}

async function onAddUser() {
  const v = await openModal({
    title: 'Новый пользователь',
    fields: [
      { name: 'login',     label: 'Логин (латиница, цифры, _)', required: true },
      { name: 'full_name', label: 'ФИО',                         required: true },
      { name: 'password',  label: 'Пароль (мин 4 символа)', type: 'password', required: true },
      { name: 'role',      label: 'Роль', type: 'select', options: ROLE_OPTIONS, value: 'manager' },
      { name: 'email',     label: 'Email (для уведомлений, необязательно)' },
    ],
    confirmLabel: 'Создать',
  });
  if (!v) return;
  if (!v.email) delete v.email;
  await api.createUser(v);
  showNotification('Пользователь создан', { type: 'success' });
  await loadAll();
  render();
}

async function onEditUser(id) {
  const u = _users.find(x => x.id === id);
  if (!u) return;
  const v = await openModal({
    title: `Редактировать ${u.login}`,
    fields: [
      { name: 'login',     label: 'Логин (латиница, цифры, _)', value: u.login },
      { name: 'full_name', label: 'ФИО',                          value: u.full_name },
      { name: 'role',      label: 'Роль', type: 'select', options: ROLE_OPTIONS, value: u.role },
      { name: 'resource_id', label: 'Ресурс (исполнитель)', type: 'select',
        options: [
          { value: '', label: '— не привязан —' },
          ..._resources.map((r) => ({ value: String(r.id), label: r.name })),
        ],
        value: u.resource_id != null ? String(u.resource_id) : '' },
      { name: 'email',     label: 'Email (для уведомлений)', value: u.email ?? '' },
      { name: 'active',    label: 'Активен',
        type: 'select', options: [{value: '1', label: 'Да'}, {value: '0', label: 'Нет'}],
        value: u.active ? '1' : '0' },
    ],
    confirmLabel: 'Сохранить',
  });
  if (!v) return;
  const body = {};
  if (v.login && v.login !== u.login) body.login = v.login;
  if (v.full_name && v.full_name !== u.full_name) body.full_name = v.full_name;
  if (v.role && v.role !== u.role) body.role = v.role;
  if ((v.email || '') !== (u.email || '')) body.email = v.email || null;
  const newRes = v.resource_id == null || v.resource_id === '' ? null : Number(v.resource_id);
  if (newRes !== u.resource_id) body.resource_id = newRes;
  const newActive = !!Number(v.active);
  if (newActive !== u.active) body.active = newActive;
  if (!Object.keys(body).length) return;
  await api.patchUser(id, body);
  showNotification('Сохранено', { type: 'success' });
  await loadAll();
  render();
}

async function onResetPwd(id) {
  const u = _users.find(x => x.id === id);
  if (!u) return;
  const v = await openModal({
    title: `Новый пароль для ${u.login}`,
    fields: [
      { name: 'password', label: 'Пароль (мин 4 символа)', type: 'password', required: true },
    ],
    confirmLabel: 'Сменить',
  });
  if (!v?.password) return;
  await api.patchUser(id, { password: v.password });
  showNotification('Пароль обновлён', { type: 'success' });
}

async function onDelUser(id) {
  const u = _users.find(x => x.id === id);
  if (!u) return;
  const confirmed = await openModal({
    title: 'Удалить пользователя?',
    message: `«${u.full_name}» (${u.login}) будет деактивирован. Hard-delete не делается — историю в audit log сохраняем.`,
    confirmLabel: 'Деактивировать',
    danger: true,
  });
  if (!confirmed) return;
  await api.deleteUser(id);
  showNotification('Пользователь деактивирован', { type: 'success' });
  await loadAll();
  render();
}

// Phase 2 Wave 3: contractor link/unlink через inline-picker.
async function onLinkContractor(userId, anchorBtn) {
  const u = _users.find(x => x.id === userId);
  if (!u) return;
  let candidates;
  try {
    candidates = await ensureContractors();
  } catch (err) {
    showNotification(err.detail || err.message || 'Ошибка загрузки контракторов', { type: 'error' });
    return;
  }
  _picker.open({
    anchor: anchorBtn,
    items: candidates,
    // С5: contractor — НЕ user. Показываем display_name + channel (tg/max).
    renderItem: (c) => {
      const ch = c.telegram_user_id ? 'tg' : (c.max_user_id ? 'max' : '—');
      return `${escapeHtml(c.display_name)} <span class="ip-channel">(${ch})</span>`;
    },
    filterText: (c) => `${c.display_name}`,
    emptyMessage: 'Нет доступных контракторов. Создаются через Mentor-бот в Telegram.',
    placeholder: 'Поиск по имени…',
    onPick: async (target) => {
      try {
        await api.patchUser(userId, { contractor_id: target.id });
        invalidateContractorsCache();
        showNotification(`Привязан контрактор «${target.display_name}»`, { type: 'success' });
        await loadAll();
        render();
      } catch (err) {
        showNotification(err.detail || err.message || 'Не удалось привязать', { type: 'error' });
      }
    },
    onCancel: () => {},
  });
}

async function onUnlinkContractor(userId) {
  const u = _users.find(x => x.id === userId);
  if (!u) return;
  const confirmed = await openModal({
    title: 'Отвязать контрактора?',
    message: `Связь с «${u.contractor_display_name}» будет снята. Telegram/MAX-бот контрактора продолжит работать.`,
    confirmLabel: 'Отвязать',
  });
  if (!confirmed) return;
  await api.patchUser(userId, { contractor_id: null });
  invalidateContractorsCache();
  showNotification('Связь снята', { type: 'success' });
  await loadAll();
  render();
}

// ---------- PORTFOLIOS ----------

function renderPortfolios() {
  // Wave 4: portfolio.crud — owner-only.
  const canEdit = canDo('portfolio.crud');
  const headerBtn = canEdit
    ? `<button class="toolbar-btn-primary" data-action="add-portfolio">+ Новый портфель</button>`
    : '';
  return `
    <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:var(--space-3);">
      <h2 style="margin:0;">Портфели</h2>
      ${headerBtn}
    </div>
    <table class="admin-table">
      <thead><tr>
        <th>Code</th><th>Название</th><th>Описание</th>
        <th>Цвет</th><th>Sort</th><th></th>
      </tr></thead>
      <tbody>
        ${_portfolios.map(p => renderPortfolioRow(p, canEdit)).join('')}
      </tbody>
    </table>`;
}

function renderPortfolioRow(p, canEdit) {
  const colorChip = p.color
    ? `<span style="display:inline-block;width:14px;height:14px;border-radius:3px;background:${p.color};vertical-align:middle"></span> <code>${escapeHtml(p.color)}</code>`
    : '—';
  const actions = canEdit
    ? `
        <button class="admin-btn-sm" data-action="edit-portfolio" data-id="${p.id}">✏</button>
        <button class="admin-btn-sm danger" data-action="del-portfolio" data-id="${p.id}">🗑</button>
      `
    : '';
  return `
    <tr>
      <td><code>${escapeHtml(p.code)}</code></td>
      <td>${escapeHtml(p.name)}</td>
      <td>${escapeHtml(p.description ?? '')}</td>
      <td>${colorChip}</td>
      <td>${p.sort_order}</td>
      <td><div class="admin-actions">${actions}</div></td>
    </tr>`;
}

function wirePortfolios() {
  _container.querySelectorAll('[data-action]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const action = e.currentTarget.dataset.action;
      const id = Number(e.currentTarget.dataset.id);
      try {
        if (action === 'add-portfolio') await onAddPortfolio();
        else if (action === 'edit-portfolio') await onEditPortfolio(id);
        else if (action === 'del-portfolio') await onDelPortfolio(id);
      } catch (err) {
        showNotification(err.detail || err.message || 'Ошибка', { type: 'error' });
      }
    });
  });
}

async function onAddPortfolio() {
  const v = await openModal({
    title: 'Новый портфель',
    fields: [
      { name: 'code',        label: 'Код (UPPER_CASE, обязательно)', required: true },
      { name: 'name',        label: 'Название',                       required: true },
      { name: 'description', label: 'Описание (опционально)' },
      { name: 'color',       label: 'Цвет (HEX, опционально)',    placeholder: '#3b82f6' },
      { name: 'sort_order',  label: 'Порядок сортировки',         type: 'number', value: 100 },
    ],
    confirmLabel: 'Создать',
  });
  if (!v) return;
  const body = {
    code: v.code,
    name: v.name,
    sort_order: v.sort_order ?? 100,
  };
  if (v.description) body.description = v.description;
  if (v.color)       body.color = v.color;
  await api.createPortfolio(body);
  showNotification('Портфель создан', { type: 'success' });
  await loadAll();
  render();
}

async function onEditPortfolio(id) {
  const p = _portfolios.find(x => x.id === id);
  if (!p) return;
  const v = await openModal({
    title: `Редактировать ${p.code}`,
    fields: [
      { name: 'code',        label: 'Код',         value: p.code },
      { name: 'name',        label: 'Название',    value: p.name },
      { name: 'description', label: 'Описание',    value: p.description ?? '' },
      { name: 'color',       label: 'Цвет (HEX)',  value: p.color ?? '' },
      { name: 'sort_order',  label: 'Порядок',     type: 'number', value: p.sort_order },
    ],
    confirmLabel: 'Сохранить',
  });
  if (!v) return;
  const body = {};
  if (v.code        && v.code        !== p.code)        body.code = v.code;
  if (v.name        && v.name        !== p.name)        body.name = v.name;
  if (v.description !== p.description)                  body.description = v.description || null;
  if (v.color       !== p.color)                        body.color = v.color || null;
  if (Number(v.sort_order) !== p.sort_order)            body.sort_order = Number(v.sort_order);
  if (!Object.keys(body).length) return;
  await api.patchPortfolio(id, body);
  showNotification('Сохранено', { type: 'success' });
  await loadAll();
  render();
}

async function onDelPortfolio(id) {
  const p = _portfolios.find(x => x.id === id);
  if (!p) return;
  const ok = await openModal({
    title: 'Удалить портфель?',
    message: `«${p.name}» (${p.code}) будет удалён. Проекты в этом портфеле потеряют привязку (portfolio_id = NULL).`,
    confirmLabel: 'Удалить',
    danger: true,
  });
  if (!ok) return;
  await api.deletePortfolio(id);
  showNotification('Портфель удалён', { type: 'success' });
  await loadAll();
  render();
}

// ---------- CONTRACTORS (Phase 2 Wave 6, read-only) ----------

function renderContractors() {
  if (_contractorsAdmin == null) {
    return `<div style="padding:var(--space-3); color:var(--text-secondary)">Загрузка списка подрядчиков…</div>`;
  }
  const rows = _contractorsAdmin;
  if (!rows.length) {
    return `
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:var(--space-3);">
        <h2 style="margin:0;">Подрядчики</h2>
        ${renderInactiveToggle()}
      </div>
      <div style="padding:var(--space-4); color:var(--text-secondary)">
        Нет подрядчиков. Создаются через Mentor-бот в Telegram.
      </div>`;
  }
  return `
    <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:var(--space-3);">
      <h2 style="margin:0;">Подрядчики</h2>
      ${renderInactiveToggle()}
    </div>
    <table class="admin-table">
      <thead><tr>
        <th>Имя</th><th>Роль</th><th>Ресурс</th>
        <th title="Telegram user_id">TG ID</th>
        <th title="MAX user_id">MAX ID</th>
        <th title="Каналы доставки: tg / max / tg+max">Бот</th>
        <th>Проекты</th>
        <th>Активен</th>
        <th>Последнее сообщение</th>
        <th>User-аккаунт</th>
      </tr></thead>
      <tbody>
        ${rows.map(renderContractorRow).join('')}
      </tbody>
    </table>
    <div style="margin-top:var(--space-3); color:var(--text-tertiary); font-size:0.85em;">
      Подрядчики создаются через Mentor-бот, эта таблица — только для просмотра.
      Связь user ↔ подрядчик настраивается на вкладке «Пользователи».
    </div>`;
}

function renderInactiveToggle() {
  return `
    <label style="display:flex; align-items:center; gap:var(--space-2); color:var(--text-secondary); font-size:0.9em; cursor:pointer;">
      <input type="checkbox" data-action="toggle-inactive" ${_showInactiveContractors ? 'checked' : ''}>
      Показать неактивных
    </label>`;
}

function renderContractorRow(c) {
  const tgId = c.telegram_user_id != null ? `<code>${c.telegram_user_id}</code>` : '—';
  const maxId = c.max_user_id != null ? `<code>${c.max_user_id}</code>` : '—';
  // Wave 6: вычисляем тип канала. По CHECK contractors_one_channel хотя бы один не NULL.
  const tg = c.telegram_user_id != null;
  const max = c.max_user_id != null;
  const channels = tg && max ? 'tg+max' : tg ? 'tg' : max ? 'max' : null;
  const maxChatHint = c.max_chat_id != null
    ? ` (max_chat_id: ${c.max_chat_id})`
    : (max ? ' (max_chat_id появится при первом сообщении в МАХ-бот)' : '');
  const mentorCell = channels
    ? `<span class="mentor-on" title="Каналы доставки: ${channels}${maxChatHint}">${channels}</span>`
    : `<span class="mentor-off" title="Нет каналов доставки">—</span>`;
  const projects = (c.projects && c.projects.length)
    ? c.projects.map(id => `<code>${id}</code>`).join(' ')
    : '—';
  const lastMsg = c.last_message_at
    ? new Date(c.last_message_at).toLocaleString('ru')
    : '—';
  const linked = c.linked_user_login
    ? `<code>${escapeHtml(c.linked_user_login)}</code>`
    : '—';
  return `
    <tr class="${c.active ? '' : 'inactive'}">
      <td>${escapeHtml(c.display_name)}</td>
      <td>${c.role ? escapeHtml(c.role) : '—'}</td>
      <td>${escapeHtml(c.resource_name ?? '—')}</td>
      <td>${tgId}</td>
      <td>${maxId}</td>
      <td>${mentorCell}</td>
      <td>${projects}</td>
      <td>${c.active ? '✓' : '✗'}</td>
      <td>${lastMsg}</td>
      <td>${linked}</td>
    </tr>`;
}

async function wireContractors() {
  if (_contractorsAdmin == null) {
    try {
      _contractorsAdmin = await api.contractorsAdminList(_showInactiveContractors);
      render();
      return;
    } catch (err) {
      showNotification(err.detail || err.message || 'Ошибка загрузки подрядчиков', { type: 'error' });
      return;
    }
  }
  // Toggle inactive — рефетч с новым параметром.
  _container.querySelectorAll('[data-action="toggle-inactive"]').forEach(cb => {
    cb.addEventListener('change', async (e) => {
      _showInactiveContractors = e.currentTarget.checked;
      try {
        _contractorsAdmin = await api.contractorsAdminList(_showInactiveContractors);
        render();
      } catch (err) {
        showNotification(err.detail || err.message || 'Ошибка загрузки', { type: 'error' });
      }
    });
  });
}

// ---------- PERMISSIONS (Phase 2 Wave 4) ----------

const ROLE_COLS = ['owner', 'admin', 'manager', 'user'];

function renderPermissions() {
  if (!_matrixData) {
    // Lazy-load — render placeholder, в wirePermissions поднимем fetch и re-render.
    return `<div style="padding:var(--space-3); color:var(--text-secondary)">Загрузка таблицы прав…</div>`;
  }

  const { actions, sections, matrix } = _matrixData;

  // Группируем actions по section.
  const bySection = {};
  for (const a of actions) {
    if (!bySection[a.section]) bySection[a.section] = [];
    bySection[a.section].push(a);
  }

  let html = `
    <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:var(--space-3);">
      <h2 style="margin:0;">Права ролей</h2>
      <span style="color:var(--text-secondary); font-size:0.9em;">Owner всегда видит/делает всё (галки нередактируемы). Изменения применяются после relogin (или 60 сек cache).</span>
    </div>
  `;

  // ── ПОРТФЕЛИ (visibility per portfolio × role) ──
  html += `
    <div class="perm-section">
      <div class="perm-section-h">Видимость портфелей (per portfolio × role)</div>
      <table class="perm-table">
        <thead><tr>
          <th>Портфель</th>
          <th>Owner</th><th>Admin</th><th>Менеджер</th><th>Пользователь</th>
        </tr></thead>
        <tbody>
          ${_portfolios.map(p => renderVisibilityRow(p)).join('')}
        </tbody>
      </table>
    </div>
  `;

  // ── ОСТАЛЬНЫЕ СЕКЦИИ (action-codes) ──
  for (const section of sections) {
    const sectionActions = bySection[section.key] || [];
    if (!sectionActions.length) continue;
    html += `
      <div class="perm-section">
        <div class="perm-section-h">${escapeHtml(section.label)}</div>
        <table class="perm-table">
          <thead><tr>
            <th>Действие</th>
            <th>Owner</th><th>Admin</th><th>Менеджер</th><th>Пользователь</th>
          </tr></thead>
          <tbody>
            ${sectionActions.map(a => renderMatrixRow(a, matrix)).join('')}
          </tbody>
        </table>
      </div>
    `;
  }
  return html;
}

function renderMatrixRow(action, matrix) {
  const scopedHint = action.scoped
    ? `<span class="perm-scoped" title="Для Менеджера/Пользователя дополнительно проверяется per-project assignment">🔧</span>`
    : '';
  const cell = (role) => {
    if (role === 'owner') {
      return `<td class="perm-row-cell perm-owner-cell" title="Собственник всегда имеет доступ; не редактируется"><input type="checkbox" checked disabled></td>`;
    }
    const checked = !!(matrix[role] && matrix[role][action.code]);
    return `<td class="perm-row-cell">
      <input type="checkbox" ${checked ? 'checked' : ''}
             data-action-cell="${action.code}" data-role="${role}">
    </td>`;
  };
  return `
    <tr>
      <td>${escapeHtml(action.label)}${scopedHint}</td>
      ${ROLE_COLS.map(cell).join('')}
    </tr>`;
}

function renderVisibilityRow(p) {
  const rows = _visibilityData[p.id];
  const cell = (role) => {
    if (role === 'owner') {
      return `<td class="perm-row-cell perm-owner-cell" title="Собственник всегда имеет доступ; не редактируется"><input type="checkbox" checked disabled></td>`;
    }
    // Если visibility ещё не загружена — disabled placeholder.
    if (!rows) {
      return `<td class="perm-row-cell"><input type="checkbox" disabled></td>`;
    }
    const r = rows.find(x => x.role === role);
    const visible = r ? r.visible : true;
    return `<td class="perm-row-cell">
      <input type="checkbox" ${visible ? 'checked' : ''}
             data-vis-cell="${p.id}" data-role="${role}">
    </td>`;
  };
  return `
    <tr>
      <td>${escapeHtml(p.name)} <code style="color:var(--text-tertiary);font-size:0.85em">${escapeHtml(p.code)}</code></td>
      ${ROLE_COLS.map(cell).join('')}
    </tr>`;
}

async function wirePermissions() {
  // Lazy-load matrix + visibility если ещё не загружены.
  if (!_matrixData) {
    try {
      _matrixData = await api.permissionsMatrix();
      // Параллельно подгружаем visibility для всех портфелей.
      const visResults = await Promise.all(
        _portfolios.map(p => api.portfolioVisibility(p.id).then(r => [p.id, r]).catch(() => [p.id, []]))
      );
      _visibilityData = Object.fromEntries(visResults);
      render();
      return;
    } catch (err) {
      showNotification(err.detail || err.message || 'Ошибка загрузки matrix', { type: 'error' });
      return;
    }
  }

  // Click handlers — optimistic update + PATCH.
  _container.querySelectorAll('input[data-action-cell]').forEach(cb => {
    cb.addEventListener('change', async (e) => {
      const code = e.currentTarget.dataset.actionCell;
      const role = e.currentTarget.dataset.role;
      const allowed = e.currentTarget.checked;
      const prev = _matrixData.matrix[role][code];
      _matrixData.matrix[role][code] = allowed;
      try {
        await api.patchPermissionsMatrix([{ role, action_code: code, allowed }]);
        showNotification('Сохранено', { type: 'success', duration: 1500 });
      } catch (err) {
        // Revert
        _matrixData.matrix[role][code] = prev;
        e.currentTarget.checked = prev;
        showNotification(err.detail || err.message || 'Не удалось сохранить', { type: 'error' });
      }
    });
  });

  _container.querySelectorAll('input[data-vis-cell]').forEach(cb => {
    cb.addEventListener('change', async (e) => {
      const pid = Number(e.currentTarget.dataset.visCell);
      const role = e.currentTarget.dataset.role;
      const visible = e.currentTarget.checked;
      const rows = _visibilityData[pid] || [];
      const existing = rows.find(x => x.role === role);
      const prev = existing ? existing.visible : true;
      if (existing) existing.visible = visible;
      else rows.push({ role, visible });
      _visibilityData[pid] = rows;
      try {
        await api.patchPortfolioVisibility(pid, [{ role, visible }]);
        showNotification('Сохранено', { type: 'success', duration: 1500 });
      } catch (err) {
        if (existing) existing.visible = prev;
        e.currentTarget.checked = prev;
        showNotification(err.detail || err.message || 'Не удалось сохранить', { type: 'error' });
      }
    });
  });
}

// ---------- RESOURCES (миграция 048: CRUD-вкладка) ----------

function renderResources() {
  const canCrud = canDo('resource.crud');
  return `
    <div class="admin-section-header" style="display:flex; align-items:center; gap:var(--space-3); margin-bottom:var(--space-3);">
      <h2 style="font-size:var(--text-lg); margin:0;">Ресурсы</h2>
      ${canCrud
        ? `<button class="admin-btn-primary" data-action="add-resource">+ Добавить ресурс</button>`
        : ''}
    </div>
    <p style="font-size:12.5px; color:var(--text-tertiary); margin-bottom:var(--space-3);">
      Список исполнителей (свои + подрядчики). Связь user ↔ resource задаётся
      на вкладке «Пользователи».
    </p>
    <table class="admin-table">
      <thead>
        <tr>
          <th>Имя</th>
          <th>Капасити, мин/день</th>
          <th>Ставка ₽/ч</th>
          <th>Скиллы</th>
          <th>Drum</th>
          <th>Outsource</th>
          <th>Создан</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        ${_resources.map((r) => renderResourceRow(r, canCrud)).join('')}
      </tbody>
    </table>
  `;
}

function renderResourceRow(r, canCrud) {
  const skills = Array.isArray(r.skills) ? r.skills : [];
  const skillsCell = skills.length
    ? `<span title="${escapeHtml(skills.join(', '))}">${escapeHtml(skills.slice(0, 3).join(', '))}${skills.length > 3 ? ` +${skills.length - 3}` : ''}</span>`
    : '<span style="color:var(--text-tertiary)">—</span>';
  const rate = r.rate_rub_per_hour != null ? Number(r.rate_rub_per_hour).toLocaleString('ru-RU') : '—';
  const cap = r.daily_capacity != null ? r.daily_capacity : '—';
  const created = r.created_at ? new Date(r.created_at).toLocaleDateString('ru-RU') : '—';
  return `
    <tr data-id="${r.id}">
      <td>${escapeHtml(r.name)}</td>
      <td style="text-align:right; font-variant-numeric:tabular-nums">${cap}</td>
      <td style="text-align:right; font-variant-numeric:tabular-nums">${rate}</td>
      <td>${skillsCell}</td>
      <td>${r.is_drum ? '✓' : ''}</td>
      <td>${r.is_outsourceable ? '✓' : ''}</td>
      <td style="color:var(--text-tertiary); font-size:12px">${created}</td>
      <td style="text-align:right; white-space:nowrap;">
        ${canCrud
          ? `<button class="admin-btn-sm" data-action="edit-resource" data-id="${r.id}">✏</button>
             <button class="admin-btn-sm danger" data-action="del-resource" data-id="${r.id}">🗑</button>`
          : ''}
      </td>
    </tr>
  `;
}

function wireResources() {
  _container.querySelectorAll('[data-action="add-resource"]').forEach((btn) => {
    btn.addEventListener('click', onAddResource);
  });
  _container.querySelectorAll('[data-action="edit-resource"]').forEach((btn) => {
    btn.addEventListener('click', () => onEditResource(Number(btn.dataset.id)));
  });
  _container.querySelectorAll('[data-action="del-resource"]').forEach((btn) => {
    btn.addEventListener('click', () => onDelResource(Number(btn.dataset.id)));
  });
}

async function onAddResource() {
  const v = await openModal({
    title: 'Новый ресурс',
    fields: _resourceFormFields({}),
    confirmLabel: 'Создать',
  });
  if (!v) return;
  try {
    await api.createResource(_resourcePayload(v));
    showNotification('Ресурс создан', { type: 'success' });
    await loadAll();
    render();
  } catch (err) {
    showNotification(err.detail || err.message || 'Ошибка', { type: 'error' });
  }
}

async function onEditResource(id) {
  const r = _resources.find((x) => x.id === id);
  if (!r) return;
  const v = await openModal({
    title: `Редактировать ${r.name}`,
    fields: _resourceFormFields(r),
    confirmLabel: 'Сохранить',
  });
  if (!v) return;
  try {
    await api.patchResource(id, _resourcePayload(v));
    showNotification('Сохранено', { type: 'success' });
    await loadAll();
    render();
  } catch (err) {
    showNotification(err.detail || err.message || 'Ошибка', { type: 'error' });
  }
}

async function onDelResource(id) {
  const r = _resources.find((x) => x.id === id);
  if (!r) return;
  const ok = await openModal({
    title: 'Удалить ресурс?',
    message: `«${r.name}» будет удалён. Если ресурс используется в задачах или у пользователей — ошибка 409.`,
    confirmLabel: 'Удалить',
    danger: true,
  });
  if (!ok) return;
  try {
    await api.deleteResource(id);
    showNotification('Ресурс удалён', { type: 'success' });
    await loadAll();
    render();
  } catch (err) {
    showNotification(err.detail || err.message || 'Не удалось удалить', { type: 'error' });
  }
}

function _resourceFormFields(r) {
  return [
    { name: 'name', label: 'Имя', value: r.name || '', required: true },
    { name: 'daily_capacity', label: 'Капасити, мин/день (480 = 8ч)',
      type: 'number', value: r.daily_capacity ?? '' },
    { name: 'rate_rub_per_hour', label: 'Ставка ₽/час',
      type: 'number', value: r.rate_rub_per_hour ?? '' },
    { name: 'skills', label: 'Скиллы (через запятую)',
      value: Array.isArray(r.skills) ? r.skills.join(', ') : '' },
    { name: 'is_drum', label: 'Ключевой ресурс (барабан)',
      type: 'select',
      options: [{ value: '0', label: 'Нет' }, { value: '1', label: 'Да' }],
      value: r.is_drum ? '1' : '0' },
    { name: 'is_outsourceable', label: 'Можно на аутсорс',
      type: 'select',
      options: [{ value: '0', label: 'Нет' }, { value: '1', label: 'Да' }],
      value: r.is_outsourceable ? '1' : '0' },
    { name: 'notes', label: 'Заметки', value: r.notes || '' },
  ];
}

function _resourcePayload(v) {
  const skills = (v.skills || '').split(',').map((s) => s.trim()).filter(Boolean);
  return {
    name: v.name,
    daily_capacity: v.daily_capacity ? Number(v.daily_capacity) : null,
    rate_rub_per_hour: v.rate_rub_per_hour ? Number(v.rate_rub_per_hour) : null,
    skills: skills.length ? skills : null,
    is_drum: v.is_drum === '1',
    is_outsourceable: v.is_outsourceable === '1',
    notes: v.notes || null,
  };
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// ---------- ПРАВИЛА ОТБОРА (префильтр Руслана, Ф-1/Ф-2, миграции 056, 058) ----------

const PF_KIND_LABEL = {
  rubric: 'Рубрика для LLM',
  regex_reject: 'Regex: отсеять',
  regex_pass: 'Regex: пропустить',
  threshold: 'Порог / условие',
};

const PF_RESULT_LABEL = {
  reject: 'Отсеять',
  review: 'Не выше «На проверку»',
  pass: 'Плюс: склонить к pass (для Марата)',
};

const PF_SCOPE_LABEL = {
  work: 'в составе работ (до названия объекта)',
  title: 'во всём названии',
};

const fmtMln = (rub) => {
  const v = Number(rub) / 1e6;
  const s = Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
  return `${s} млн ₽`;
};

// Текст колонки «Правило» для порога: итог, НМЦК, регион, regex по названию.
function thresholdSummary(r) {
  const p = r.params ?? {};
  const parts = [`<b>${escapeHtml(PF_RESULT_LABEL[p.result] ?? p.result ?? '?')}</b>`];
  if (p.price_below != null) parts.push(`НМЦК ниже ${escapeHtml(fmtMln(p.price_below))}`);
  if (p.regions) parts.push(`регион/город: <code>${escapeHtml(p.regions)}</code>`);
  if (r.pattern_or_text) parts.push(`название: <code>${escapeHtml(r.pattern_or_text)}</code>`);
  return parts.join('<br>');
}

function ruleCell(r) {
  if (r.kind === 'rubric') return `<div class="pf-rubric">${escapeHtml(r.pattern_or_text)}</div>`;
  if (r.kind === 'threshold') return thresholdSummary(r);
  const scope = r.params?.scope === 'title' ? '<br><small>во всём названии</small>' : '';
  return `<code>${escapeHtml(r.pattern_or_text)}</code>${scope}`;
}

// Поля формы по типу правила (r — существующее правило при правке).
function ruleFields(kind, r = null) {
  const p = r?.params ?? {};
  const reason = { name: 'reason', label: 'Причина (видна в карточке)', value: r?.reason ?? '',
    placeholder: 'например: Дёшево для крупного города' };
  if (kind === 'rubric') {
    return [
      { name: 'pattern_or_text', label: 'Текст рубрики', type: 'textarea', rows: 14, required: true,
        value: r?.pattern_or_text ?? '' },
      reason,
    ];
  }
  if (kind === 'threshold') {
    return [
      { name: 'result', label: 'Итог', type: 'select', value: p.result ?? 'reject',
        options: Object.entries(PF_RESULT_LABEL).map(([value, label]) => ({ value, label })) },
      { name: 'price_mln', label: 'НМЦК ниже, млн ₽ (пусто — без порога суммы)', type: 'number',
        min: 0, step: 'any', value: p.price_below != null ? p.price_below / 1e6 : '' },
      { name: 'regions', label: 'Регион / город / заказчик — regex (пусто — любой)', value: p.regions ?? '',
        placeholder: 'например: санкт петербург|водоканал санкт' },
      { name: 'pattern_or_text', label: 'Regex по названию закупки (пусто — любое)', type: 'textarea', rows: 3,
        value: r?.pattern_or_text ?? '', placeholder: 'например: очистн\\w* сооружени' },
      reason,
    ];
  }
  return [
    { name: 'pattern_or_text', label: 'Regex', type: 'textarea', rows: 4, required: true,
      value: r?.pattern_or_text ?? '', placeholder: 'например: ^оказани\\w* услуг\\w* по уборк' },
    { name: 'scope', label: 'Где искать', type: 'select', value: p.scope ?? 'work',
      options: Object.entries(PF_SCOPE_LABEL).map(([value, label]) => ({ value, label })) },
    reason,
  ];
}

// Значения формы → тело запроса (params по типу правила).
function ruleBody(kind, v) {
  const body = { pattern_or_text: v.pattern_or_text ?? '', reason: v.reason };
  if (kind === 'threshold') {
    body.params = {
      result: v.result,
      price_below: v.price_mln != null && !Number.isNaN(v.price_mln) ? Math.round(v.price_mln * 1e6) : null,
      regions: v.regions ?? null,
    };
  } else if (kind !== 'rubric') {
    body.params = { scope: v.scope ?? 'work' };
  }
  return body;
}

const isOwner = () => getCurrentUser()?.role === 'owner';

async function loadPrefilterRules() {
  try {
    _prefilterRules = await api.tenders.prefilterRules();
  } catch (err) {
    _prefilterRules = { version: 0, items: [], error: err.detail || err.message || 'Ошибка' };
  }
  if (_activeTab === 'prefilter') render();
}

function renderPrefilterRules() {
  if (_prefilterRules == null) {
    loadPrefilterRules();
    return '<div class="loading-spinner">Загрузка правил...</div>';
  }
  const edit = isOwner();
  const rows = _prefilterRules.items.map((r) => `
    <tr class="${r.is_active ? '' : 'admin-row-inactive'}" data-rule-id="${r.id}">
      <td>${escapeHtml(PF_KIND_LABEL[r.kind] ?? r.kind)}</td>
      <td>${ruleCell(r)}</td>
      <td>${escapeHtml(r.reason ?? '')}</td>
      <td style="text-align:center;">${r.version}</td>
      <td style="text-align:center;">
        <input type="checkbox" data-action="pf-toggle" data-id="${r.id}"${r.is_active ? ' checked' : ''}${edit ? '' : ' disabled'}
          title="${r.is_active ? 'Включено' : 'Выключено'}">
      </td>
      <td>${edit && r.is_active ? `<button class="admin-btn-sm" data-action="pf-edit" data-id="${r.id}">Изменить</button>` : ''}</td>
    </tr>`).join('');
  return `
    <div class="admin-section-header" style="display:flex; align-items:center; gap:var(--space-3); margin-bottom:var(--space-3);">
      <h2 style="font-size:var(--text-lg); margin:0;">Правила первичного отбора</h2>
      <span style="color:var(--text-tertiary);">версия ${_prefilterRules.version}</span>
      ${edit ? '<button class="admin-btn-primary" data-action="pf-add">+ Добавить правило</button>' : ''}
    </div>
    ${_prefilterRules.error ? `<p style="color:var(--text-danger);">${escapeHtml(_prefilterRules.error)}</p>` : ''}
    <p style="font-size:12.5px; color:var(--text-tertiary); margin-bottom:var(--space-3); max-width:900px;">
      Закупки из МЭЛТОР сначала проверяются regex-правилами и порогами (сразу при импорте), остальные раз в час
      оценивает LLM по рубрике. Отсеянные не удаляются: они закрыты и видны в «Аукционах» по чипу «Отсеяно».
      Regex ищется без учёта регистра в составе работ — в названии закупки до названия объекта
      («по объекту …», «…») — или во всём названии; дефисы считаются пробелами.
      Порог срабатывает, если выполнены все заданные условия: НМЦК ниже порога, регион/город/заказчик и
      название подходят под regex. «Отсеять» решает сразу; «Не выше „На проверку“» не даёт LLM пропустить
      карточку; «Плюс» переводит карточку «На проверку» в пропущенные с пометкой «Для Марата».
      Любая правка повышает версию правил, и карточки,
      оценённые автоматически, пересматриваются. Ручные «Вернуть в работу» / «Отсеять» не пересматриваются
      и попадают в промпт как примеры.
      ${edit ? '' : ' Менять правила может только Собственник.'}
    </p>
    <table class="admin-table">
      <thead><tr><th>Тип</th><th>Правило</th><th>Причина</th><th>Версия</th><th>Вкл.</th><th></th></tr></thead>
      <tbody>${rows || '<tr><td colspan="6">Правил нет.</td></tr>'}</tbody>
    </table>
  `;
}

function wirePrefilterRules() {
  _container.querySelector('[data-action="pf-add"]')?.addEventListener('click', onAddPrefilterRule);
  _container.querySelectorAll('[data-action="pf-edit"]').forEach((btn) => {
    btn.addEventListener('click', () => onEditPrefilterRule(Number(btn.dataset.id)));
  });
  _container.querySelectorAll('[data-action="pf-toggle"]').forEach((cb) => {
    cb.addEventListener('change', () => onTogglePrefilterRule(Number(cb.dataset.id), cb.checked));
  });
}

async function savePrefilter(call, okText) {
  try {
    await call();
    showNotification(okText, { type: 'success', duration: 1500 });
  } catch (err) {
    showNotification(err.detail || err.message || 'Не удалось сохранить', { type: 'error' });
  }
  _prefilterRules = null;
  await loadPrefilterRules();
}

async function onAddPrefilterRule() {
  const k = await openModal({
    title: 'Новое правило отбора',
    confirmLabel: 'Дальше',
    fields: [
      { name: 'kind', label: 'Тип', type: 'select', value: 'threshold',
        options: Object.entries(PF_KIND_LABEL).map(([value, label]) => ({ value, label })) },
    ],
  });
  if (!k?.kind) return;
  const v = await openModal({
    title: `Новое правило: ${PF_KIND_LABEL[k.kind] ?? k.kind}`,
    confirmLabel: 'Добавить',
    fields: ruleFields(k.kind),
  });
  if (!v) return;
  await savePrefilter(
    () => api.tenders.createPrefilterRule({ kind: k.kind, ...ruleBody(k.kind, v) }),
    'Правило добавлено',
  );
}

async function onEditPrefilterRule(id) {
  const r = _prefilterRules?.items.find((x) => x.id === id);
  if (!r) return;
  const v = await openModal({
    title: `Изменить: ${PF_KIND_LABEL[r.kind] ?? r.kind}`,
    confirmLabel: 'Сохранить',
    message: 'Сохранится новой версией; прежнее правило останется в списке выключенным.',
    fields: ruleFields(r.kind, r),
  });
  if (!v) return;
  await savePrefilter(
    () => api.tenders.patchPrefilterRule(id, ruleBody(r.kind, v)),
    'Правило сохранено',
  );
}

async function onTogglePrefilterRule(id, active) {
  await savePrefilter(
    () => api.tenders.patchPrefilterRule(id, { is_active: active }),
    active ? 'Правило включено' : 'Правило выключено',
  );
}
